import React, { useState } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   Button,
   Input,
   FormControl,
   FormLabel,
   FormErrorMessage,
   FormHelperText,
   InputGroup,
   InputLeftAddon,
   Text,
   Box,
   Spinner,
   useToast,
   VStack,
   HStack,
} from '@chakra-ui/react';
import { getAccount, updateAccount } from '../../redux/actions/account';
import { useDispatch } from 'react-redux';

const MobileNoForm = ({
   isOpen,
   onClose,
   accountId,
   glGroupId,
   name,
   shareToWhatsApp,
   setIsProcessingWp,
   isProcessingWp,
   onSuccess,
}) => {
   const dispatch = useDispatch();
   const [mobileNo, setMobileNo] = useState('');
   const [error, setError] = useState('');
   const [isSubmitting, setIsSubmitting] = useState(false);
   const toast = useToast();

   // Mobile number validation
   const validateMobileNumber = (mobile) => {
      const mobileRegex = /^[6-9]\d{9}$/; // Indian mobile number format
      return mobileRegex.test(mobile);
   };

   const handleChange = (e) => {
      const value = e.target.value;

      // Only allow digits
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
         setMobileNo(numericValue);
      }

      // Clear error when user starts typing
      if (error) {
         setError('');
      }
   };

   const handleSubmit = async () => {
      if (!mobileNo) {
         setError('Mobile number is required');
         return;
      }

      if (!validateMobileNumber(mobileNo)) {
         setError('Please enter a valid 10-digit mobile number');
         return;
      }

      setIsSubmitting(true);

      try {
         // Simulate API call
         await new Promise((resolve) => setTimeout(resolve, 2000));

         toast({
            title: 'Success!',
            description: 'Mobile number submitted successfully!',
            status: 'success',
            duration: 3000,
            isClosable: true,
         });

         await dispatch(
            updateAccount({
               mobile: mobileNo,
               id: accountId,
               glGroupId: glGroupId,
               name: name,
            }),
         );

         localStorage.setItem('mobileNo', mobileNo);

         // Parent ko updated mobile number भेजें
         if (onSuccess) {
            onSuccess(mobileNo);
         }

         if (setIsProcessingWp) {
            setIsProcessingWp(true);
         }
         shareToWhatsApp();

         // Reset form and close modal
         setMobileNo('');
         setError('');
         onClose();
      } catch (error) {
         setError('Error submitting mobile number. Please try again.');
         toast({
            title: 'Error',
            description: 'Error submitting mobile number. Please try again.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });

         if (setIsProcessingWp) {
            setIsProcessingWp(false);
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   const handleClose = () => {
      if (!isSubmitting) {
         setMobileNo('');
         setError('');
         onClose();
      }
   };

   const isValid = mobileNo && validateMobileNumber(mobileNo) && !error;

   return (
      <>
         <Modal
            isOpen={isOpen}
            onClose={handleClose}
            closeOnOverlayClick={!isSubmitting}
            closeOnEsc={!isSubmitting}
            isCentered
            size="md">
            <ModalOverlay bg="blackAlpha.600" />
            <ModalContent mx={4}>
               <ModalHeader fontSize="xl" fontWeight="bold" color="gray.800" pb={2}>
                  Enter Mobile Number
               </ModalHeader>

               <ModalCloseButton
                  isDisabled={isSubmitting}
                  _disabled={{
                     opacity: 0.4,
                     cursor: 'not-allowed',
                  }}
               />

               <ModalBody pb={6}>
                  <VStack spacing={4} align="stretch">
                     <Text color="gray.600" fontSize="sm">
                        Please enter your mobile number to continue
                     </Text>

                     <FormControl isInvalid={!!error} isRequired>
                        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                           Mobile Number
                        </FormLabel>

                        <InputGroup>
                           <InputLeftAddon children="+91" bg="gray.50" color="gray.500" fontSize="sm" />
                           <Input
                              type="tel"
                              value={mobileNo}
                              onChange={handleChange}
                              placeholder="Enter 10-digit mobile number"
                              maxLength={10}
                              isDisabled={isSubmitting}
                              focusBorderColor="blue.500"
                              errorBorderColor="red.500"
                              bg={error ? 'red.50' : 'white'}
                           />
                        </InputGroup>

                        {error && (
                           <FormErrorMessage fontSize="sm">
                              <HStack spacing={1}>
                                 <Text>⚠️</Text>
                                 <Text>{error}</Text>
                              </HStack>
                           </FormErrorMessage>
                        )}

                        {isValid && (
                           <FormHelperText color="green.600" fontSize="sm">
                              <HStack spacing={1}>
                                 <Text>✅</Text>
                                 <Text>Valid mobile number</Text>
                              </HStack>
                           </FormHelperText>
                        )}
                     </FormControl>

                     {mobileNo && (
                        <Box p={3} bg="gray.50" rounded="lg">
                           <Text fontSize="sm" color="gray.600">
                              <Text as="span" fontWeight="bold">
                                 Preview:
                              </Text>{' '}
                              +91 {mobileNo}
                           </Text>
                        </Box>
                     )}
                  </VStack>
               </ModalBody>

               <ModalFooter gap={3}>
                  <Button variant="outline" onClick={handleClose} isDisabled={isSubmitting} flex={1} colorScheme="gray">
                     Cancel
                  </Button>

                  <Button
                     colorScheme="blue"
                     onClick={handleSubmit}
                     isLoading={isSubmitting}
                     loadingText="Submitting"
                     spinner={<Spinner size="sm" />}
                     flex={1}
                     _loading={{
                        bg: 'gray.400',
                        cursor: 'not-allowed',
                     }}>
                     Submit
                  </Button>
               </ModalFooter>
            </ModalContent>
         </Modal>
      </>
   );
};

export default MobileNoForm;
